// Build Line - typed browser client for /api/build.
//
// Every shape below is derived from the router (src/routes/build-admin.ts), the
// enrollment library (src/lib/build/enrollment.ts) and the preflight module
// (src/processors/build/preflight.ts). The router is the contract; the design
// doc is not.
//
// SECURITY: an order borrows three customer credentials (registrar blob,
// sequencer key, MasterInbox key) plus one password per mailbox. None of them
// can reach this file by accident - the repo's selects carry only `has_*`
// booleans and `secrets_purged_at`, and the probe endpoint takes keys in a
// request body, uses them once and echoes nothing back. No type here holds a
// secret and none should ever be given one. Keys typed into the enrollment go
// UP the wire only.
//
// Timestamps arrive as ISO strings.

import { ApiError, api } from '../../api/client';
import { compactBuildPhotos } from './photo-upload';

export type { ApiError } from '../../api/client';

const BASE = '/api/build';

/* ─── Constants shared with the server ──────────────────────────────────── */

/** v1 connects MasterInbox through ONE trusted OAuth app on every batch; the
 *  station refuses anything else as a permanent `mi_blocked_app`. Mirrors
 *  MI_ALLOWED_APP_ID in src/lib/build/enrollment.ts. */
export const MI_ALLOWED_APP_ID = 'google-6';

/** The same cap the S7 photos station and the enrollment enforce
 *  (MAX_ENROLLMENT_PHOTO_BYTES). Checked in the browser too so an operator
 *  learns about an oversized face while they still have the file open. */
export const MAX_PHOTO_BYTES = 2 * 1024 * 1024;

/** What each registrar's credential blob has to carry. Mirrors each adapter's
 *  `credsFields` in src/lib/registrars; the enrollment quotes the same shape back. */
export const REGISTRAR_CREDS_FIELDS: Record<string, string[]> = {
  dynadot: ['api_key'],
  spaceship: ['key', 'secret'],
  ionos: ['api_key'],
  porkbun: ['api_key', 'secret_api_key'],
};

/** The four registrars, in picker order, with the registry adapters' labels.
 *  `hint` is the credential format the adapter validates. */
export const REGISTRARS: { value: BuildRegistrarId; label: string; hint: string }[] = [
  { value: 'dynadot', label: 'Dynadot', hint: 'api3 API key' },
  { value: 'spaceship', label: 'Spaceship', hint: 'API key and secret' },
  { value: 'ionos', label: 'IONOS', hint: 'API key in the prefix.secret form' },
  { value: 'porkbun', label: 'Porkbun', hint: 'pk1_ API key and sk1_ secret key; API ACCESS on per domain' },
];

/* ─── Types ─────────────────────────────────────────────────────────────── */

/** `registrar`: records written at the registrar. `spaceship` is its legacy
 *  name (registrar DNS at Spaceship), still legal on old rows and snapshots. */
export type BuildDnsMode = 'cf_held' | 'our_pool' | 'customer_cf' | 'spaceship' | 'registrar';
export type BuildRegistrarId = 'dynadot' | 'spaceship' | 'ionos' | 'porkbun';
export type BuildRegistrar = BuildRegistrarId | 'none';
export type BuildSequencerProvider = 'instantly' | 'smartlead' | 'plusvibe' | 'bison' | 'none';
export type BuildPanelBucket = 'serken' | 'regular' | 'mixed';
export type BuildAllocationPolicy = 'exact_fill' | 'leave_0_or_2';

/** One profile-configured DNS record beyond the fixed SPF/DMARC/MX set. Mirrors
 *  BuildExtraRecord in src/lib/build/types.ts. `host` is a PREFIX TEMPLATE (`@`,
 *  `_dmarc`, `inst`, any sub) that the planner expands per sending domain through
 *  recordName; `proxied` applies to A/CNAME, `priority` to MX; `ttl` defaults to
 *  3600 server-side. A disabled extra is planned as nothing at all. */
export interface BuildExtraRecord {
  type: 'TXT' | 'CNAME' | 'A' | 'MX';
  host: string;
  content: string;
  proxied?: boolean;
  priority?: number;
  ttl?: number;
  enabled: boolean;
}

/** The template / routing card. No secrets: the only credential reference is
 *  `cf_account_id`, whose token already lives encrypted on the Cloudflare row. */
export interface BuildProfile {
  id: number;
  name: string;
  customer: string;
  dns_mode: BuildDnsMode;
  cf_account_id: number | null;
  registrar_default: BuildRegistrar;
  records: {
    write_mx: boolean;
    dmarc_policy: string;
    /** Editable now; PREVIEW_SENDING_SPF is the fallback the planner uses when
     *  this is blank. An override REPLACES the string, never adds a second
     *  `v=spf1` (that is a permerror for the whole domain). */
    spf: string;
    tracking_prefix: string;
    tracking_target: string;
    /** Every record beyond the fixed SPF/DMARC/MX set - the tracking CNAMEs
     *  (`inst`, `trk`) live here now, not as hardcoded planner clauses. */
    extra_records: BuildExtraRecord[];
  };
  panel_bucket: BuildPanelBucket;
  serken_percent?: number;
  allocation_policy: BuildAllocationPolicy;
  sequencer_provider: BuildSequencerProvider;
  sequencer_settings: Record<string, unknown>;
  tag_scheme: {
    standard_tags: string[];
    optional_tags: string[];
    group_tags_enabled: boolean;
  };
  masterinbox: { enabled_default: boolean; app_id: string };
  /** `enabled` stands up the forwarding redirect (A records + rule); when it is
   *  off no A record is planned even if the order carries a target URL.
   *  `require_target_url` only bites when `enabled` is true. */
  forwarding: { require_target_url: boolean; enabled: boolean };
  photos: { enabled_default: boolean; visibility_pass: boolean };
  /** Whether Instantly's custom-tracking-domain (CTD) assertion defaults on for
   *  this profile's orders. Top-level scalar column (migration 068). */
  ctd_default: boolean;
  notes: string;
  created_at: string;
  updated_at: string;
}

/** Everything POST /profiles and PATCH /profiles/:id accept. The router's zod
 *  schema defaults every field, so the form always sends a complete object. */
export type BuildProfileInput = Omit<BuildProfile, 'id' | 'created_at' | 'updated_at'>;

/** Read-only probes. Nothing here is stored server-side; the MasterInbox branch
 *  is the reason the endpoint exists, because it is where an operator READS the
 *  workspace id the order snapshot then has to declare. */
export interface BuildProbeReport {
  cloudflare: { ok: boolean; account_id: string | null; zones: number | null; error: string | null } | null;
  sequencer: {
    ok: boolean;
    provider: string;
    workspace: string | null;
    /** PlusVibe / Bison: everything the key can list, so the wizard offers a
     *  picker instead of asking the operator to type an id from memory. */
    workspaces?: { id: string; name: string }[];
    error: string | null;
  } | null;
  masterinbox: {
    ok: boolean;
    app_id: string;
    workspace_id: number | null;
    workspace_name: string | null;
    error: string | null;
  } | null;
}

export interface BuildGateResult {
  ok: boolean;
  problems: string[];
}

/** The dry-run report. `pass` deliberately excludes the capacity gate: panel
 *  capacity is REPORTED, not gated (an order may launch and wait for seats). */
export interface BuildPreflightReport {
  pass: boolean;
  checked_at: string;
  snapshot_fingerprint: string;
  input: BuildGateResult;
  dns: BuildGateResult & { mode: string; checked: number };
  google: BuildGateResult & { conflicts: string[]; unverified: string[] };
  keys: BuildGateResult & {
    registrar: { registrar: string; checked: number } | null;
    sequencer: { provider: string; workspace: string | null } | null;
    masterinbox: { workspace_id: number; workspace_name: string | null; declared: number | null } | null;
  };
  panels: BuildGateResult & {
    buckets: string[];
    pool_panels: number;
    free_seats: number;
    run_ids: string[];
    pulled_at: string | null;
  };
  allocation: {
    ok: boolean;
    panels: { panel_email: string; kind?: 'serken' | 'regular'; domains?: number; take: number; remaining: number }[];
    by_bucket?: {
      kind: 'serken' | 'regular'; requested_domains: number; placed_domains: number;
      mailboxes: number; panels: number; shortfall_domains: number;
    }[];
    leftovers: string[];
    domains: number;
  };
}

/** One refusal from the enrollment, addressed at the field that caused it. */
export interface BuildEnrollmentIssue {
  path: string;
  message: string;
}

/** A photo as the snapshot carries it: an https URL, or inline bytes. */
export type BuildPhotoEntry = { url: string } | { b64: string; mime?: string };

export type BuildOrderStatus =
  | 'draft'
  | 'preflight'
  | 'dry_run_complete'
  | 'running'
  | 'needs_attention'
  | 'awaiting_capacity'
  | 'delivered'
  | 'closed'
  | 'aborted';

export interface BuildOrderRow {
  id: number;
  profile_id: number;
  label: string;
  status: BuildOrderStatus;
  phase: string;
  /** When the coordinator entered `phase`. The zones station's 90-minute
   *  activation deadline is anchored on it, so it is also what the board's
   *  per-phase elapsed reads. */
  phase_started_at: string | null;
  overrides: Record<string, unknown>;
  forwarding_url: string | null;
  callback_url: string | null;
  has_registrar_creds: boolean;
  has_sequencer_api_key: boolean;
  has_masterinbox_api_key: boolean;
  secrets_purged_at: string | null;
  /** Set when the snapshot's provider was `none`: the sequencer station leaves no
   *  per-mailbox stamps at all, and this is what says "skipped" rather than
   *  "not started". Both hard gates that depend on it drop out with it. */
  sequencer_skipped_at: string | null;
  sequencer_run_id: string | null;
  delivered_at: string | null;
  delivery_verified_at?: string | null;
  closed_at: string | null;
  launched_at?: string | null;
  created_at: string;
  updated_at: string;
}

/** A row of GET /orders: the order plus the counts the board renders. Every
 *  count is derived server-side from stamps and EXCLUDES abandoned items, so a
 *  "Continue anyway" order's progress can still reach its total. */
export interface BuildOrderListRow extends BuildOrderRow {
  domains_total: number;
  domains_dkim_active: number;
  domains_forwarded: number;
  mailboxes_total: number;
  mailboxes_licensed: number;
  mailboxes_renamed: number;
  mailboxes_login_proved: number;
  mailboxes_sequenced: number;
  mailboxes_warmed: number;
  mailboxes_masterinbox: number;
  /** Items carrying a live PARKED error (soft codes excluded - they never block
   *  anything and an inbox that counts them is one nobody reads). */
  parked_items: number;
  /** The hard-gate checklist as a ratio. The list has no room for the checklist
   *  itself; the detail view renders it in full. */
  hard_gate: { done: number; total: number };
}

export interface BuildOrderFilters {
  search: string;
  status: BuildOrderStatus | 'all' | 'active';
  exclude_aborted: boolean;
}

/** One parked item, from GET /attention: every order still being worked, newest
 *  failure first. Closed and aborted orders are excluded server-side. */
export interface BuildAttentionRow {
  order_id: number;
  order_label: string;
  status: BuildOrderStatus;
  kind: 'domain' | 'mailbox' | 'panel';
  item_key: string;
  error_code: string;
  error_at: string;
  explanation?: DeliveryIssueExplanation;
}

export interface BuildPanelItem {
  order_id: number;
  panel_email: string;
  claimed_at: string | null;
  licensed_at: string | null;
  error_code: string | null;
  error_at: string | null;
  updated_at: string;
}

export interface BuildDomainItem {
  order_id: number;
  domain: string;
  position: number;
  shape: 'root' | 'sub';
  registrable_root: string;
  /** Random roster mailbox that fills a DMARC `<collector>` for this domain. */
  dmarc_collector?: string | null;
  panel_email: string | null;
  zone_id: string | null;
  records_at: string | null;
  zone_active_at: string | null;
  added_at: string | null;
  verified_at: string | null;
  dkim_active_at: string | null;
  forwarding_at: string | null;
  tracking_active_at: string | null;
  error_code: string | null;
  error_at: string | null;
  /** "Continue anyway": built without this item. The error_code stays so reports
   *  still count it as failed; every gate treats it as finished. */
  abandoned_at: string | null;
  updated_at: string;
}

export interface BuildMailboxItem {
  order_id: number;
  final_email: string;
  domain: string;
  position: number;
  panel_email: string | null;
  holding_email: string | null;
  first_name: string;
  last_name: string;
  group_tag: string | null;
  claimed_at: string | null;
  licensed_at: string | null;
  renamed_at: string | null;
  photo_at: string | null;
  imaps_pushed_at: string | null;
  login_proof_at: string | null;
  seq_connected_at: string | null;
  warmup_enabled_at: string | null;
  mi_connected_at: string | null;
  error_code: string | null;
  error_at: string | null;
  abandoned_at: string | null;
  updated_at: string;
}

/** One station on the board. The counts are the coordinator's OWN gate and done
 *  predicates run over this order's items (the router evaluates the real station
 *  definitions), not a re-derivation: `ready` is work it could dispatch right
 *  now, `waiting` is everything else - which is where parked items sit, because
 *  a gate refuses them. */
export interface BuildStationCard {
  name: string;
  scope: 'order' | 'panel' | 'domain' | 'mailbox-batch';
  queue: 'build' | 'panel';
  total: number;
  done: number;
  ready: number;
  waiting: number;
}

export interface BuildPhaseRow {
  name: string;
  current: boolean;
  stations: BuildStationCard[];
}

/** The hard-gate checklist that decides `delivered`. `failures` is already
 *  operator-facing English ("3 mailbox(es) not renamed"). */
export interface BuildHardGates {
  pass: boolean;
  failures: string[];
}

/** One soft tail's verdict from the last finisher sweep. `clear` is the only
 *  field the close reads; `state` is what the checklist renders. */
export interface BuildTailResult {
  clear: boolean;
  state: string;
  detail?: Record<string, unknown>;
}

/** The finisher sweep's results row. Absent keys mean the sweep threw before it
 *  got that far; `closed` is false while a tail is still trailing. */
export interface BuildFinisherResults {
  tracking?: BuildTailResult;
  forwarding?: BuildTailResult;
  visibility?: BuildTailResult;
  closed?: boolean;
}

/* The delivery report's wire shape. Mirrors src/lib/build/delivery-report-types.ts
   verbatim - change both or neither. */

/** Google, read through three oracles: seats (mailboxes present + licensed),
 *  domains (verified on the panel) and dns (records + DKIM key live in
 *  Cloudflare). `read_error` names whichever of the three could not read. */
export interface DeliveryStackGoogle {
  pass: boolean;
  mailboxes_present: number;
  licensed: number;
  domains_verified: number;
  dkim_active: number;
  dns_ok: number;
  read_error?: string;
}

export interface DeliveryStackImaps {
  pass: boolean;
  login_proofs: number;
  read_error?: string;
}

/** Where a stack's counts came from. `live` is the oracle's own read of the
 *  platform; `ledger` means the oracle could not read it (key purged at close,
 *  no oracle for this provider yet, the workspace walk failed) and the counts
 *  are our build stamps instead - stated, never passed off as live. A ledger
 *  stack always carries `read_error` saying why. */
export type DeliveryStackSource = 'live' | 'ledger';

export interface DeliveryStackSequencer {
  pass: boolean;
  provider: string;
  skipped: boolean;
  source: DeliveryStackSource;
  workspace: { id: string | null; name: string | null };
  connected: number;
  verified: number;
  warmup_active: number;
  /** Live: accounts with a tracking domain attached. Ledger: DOMAINS with
   *  `tracking_active_at` (the stamp is per domain). */
  tracking_attached: number;
  /** Whether the order asked for tracking at all (snapshot `tracking_attach`);
   *  `tracking 0/N` on an order that never asked reads as N failures. */
  tracking_requested: boolean;
  read_error?: string;
}

export interface DeliveryStackMasterinbox {
  pass: boolean;
  enabled: boolean;
  source: DeliveryStackSource;
  workspace: { id: number | null; name: string | null };
  /** Live: healthy channels. Ledger: mailboxes stamped `mi_connected_at`. */
  healthy: number;
  read_error?: string;
}

/** One step of the build, from the item stamps: when the first item reached
 *  it, when the last one did, how many did. `count: 0` with null bounds means
 *  the step did not apply to this order (photos off, no forwarding target). */
export interface DeliveryTimelineRow {
  step: string;
  label: string;
  first: string | null;
  last: string | null;
  count: number;
}

export interface DeliveryReport {
  generated_at: string;
  /** `partial` = an oracle could not read its source, so a stack is unproven
   *  (a thrown read, or a source-level finding such as a purged key - those
   *  set `read_error` and never appear in `issues`);
   *  `issues` = every source was read and at least one stack has findings. */
  verdict: 'all_clear' | 'issues' | 'partial';
  totals: {
    domains: number;
    mailboxes: number;
    abandoned_domains: number;
    abandoned_mailboxes: number;
  };
  timeline: {
    launched_at: string | null;
    delivered_at: string | null;
    duration_seconds: number | null;
    rows: DeliveryTimelineRow[];
  };
  stacks: {
    google: DeliveryStackGoogle;
    imaps: DeliveryStackImaps;
    sequencer: DeliveryStackSequencer;
    masterinbox: DeliveryStackMasterinbox;
  };
  /** Every oracle finding, flattened, tagged with the stack it belongs to. */
  issues: { stack: string; code: string; count: number; sample: string[] }[];
  /** Informational findings that never held delivery (a tracking host still
   *  attaching when the check ran). Absent on reports stored before notes existed. */
  notes?: { stack: string; code: string; count: number; sample: string[] }[];
}

/** Human-facing meaning derived from the saved evidence; never a new QA verdict. */
export interface DeliveryIssueExplanation {
  /** `note` explains a report note: informational, never the overall kind. */
  kind: 'action' | 'verification' | 'history' | 'clear' | 'note';
  code: string;
  stack: string;
  count: number;
  summary: string;
  evidence: string;
  customerImpact: string;
  piAction: string;
}

export interface DeliveryExplanation {
  kind: Exclude<DeliveryIssueExplanation['kind'], 'note'>;
  summary: string;
  evidence: string;
  customerImpact: string;
  piAction: string;
  customerMessage: string;
  issues: DeliveryIssueExplanation[];
}

/** A stamp's window across the order's items: when the first one landed, when
 *  the last one did, and how many carry it. Derived, never stored. */
export interface BuildStampWindow {
  first: string;
  last: string;
  count: number;
}

/** GET /orders/:id. `preflight` is the stored report of the last preflight run;
 *  a run that crashed stores `{ pass: false, error }` instead, so every field
 *  beyond `pass` is optional here. */
export interface BuildOrderDetail {
  delivery_progress?: {
    id: string;
    started_at: string;
    finished_at: string | null;
    checks: { kind: string; state: 'queued' | 'running' | 'passed' | 'failed' | 'read_error' | 'incomplete';
      started_at: string | null; finished_at: string | null }[];
  } | null;
  order: BuildOrderRow;
  profile: BuildProfile | null;
  panels: BuildPanelItem[];
  domains: BuildDomainItem[];
  mailboxes: BuildMailboxItem[];
  /** Live BullMQ state of each panel's browser job, so a retrying job reads as
   *  "attempt 2 of 4" instead of an unremarkable "waiting" until the final
   *  attempt parks. Empty when Redis cannot answer or no jobs exist. */
  browser_jobs: { panel_email: string; state: string; attempts_made: number; attempts: number | null }[];
  board: BuildPhaseRow[];
  hard_gates: BuildHardGates | null;
  finishers: BuildFinisherResults | null;
  sequencer_progress?: { awaiting_configuration: string[]; retry_pending: { email: string; code: string; next_retry_at: string }[] };
  /** The live QA behind `delivered`: the report itself, `{ error }` when the
   *  station could not produce one, null when it has not run yet. */
  delivery_report: DeliveryReport | { error: string } | null;
  delivery_explanation?: DeliveryExplanation;
  performance?: {
    execution_version: number;
    target_seconds: number;
    elapsed_seconds: number | null;
    full_roster_verified: boolean;
    target_met: boolean | null;
    mailboxes: number;
    abandoned_mailboxes: number;
    abandoned_domains: number;
    events: number;
    failure_events: number;
    deferral_events: number;
    stations: { station: string; invocations: number; completed: number; total_job_seconds: number | null; p95_job_seconds: number | null }[];
    waits: { station: string; event: string; code: string | null; count: number; scheduled_wait_seconds: number }[];
  };
  preflight: (Partial<BuildPreflightReport> & { pass?: boolean; error?: string }) | null;
  timings: {
    phase: string;
    phase_started_at: string | null;
    stamps: Record<string, BuildStampWindow>;
  };
  attention: {
    kind: 'domain' | 'mailbox' | 'panel';
    item_key: string;
    error_code: string;
    error_at: string | null;
    parked: boolean;
    explanation?: DeliveryIssueExplanation;
  }[];
  /** The release watch: the operator-armed hourly retry of domains another
   *  Workspace still holds (domain_already_in_use). `until` null = not armed;
   *  `held` counts the live domains it is (or would be) waiting on. */
  release_retry: { until: string | null; last_at: string | null; held: number };
  /** Who aborted the order and why, from its event history. Null unless the
   *  order is aborted; also null for orders aborted before this was recorded. */
  abort?: { reason: string | null; operator: string | null; occurred_at: string } | null;
}

export interface BuildWorkerWarning {
  worker: string;
  provider: string;
  code: string;
  failures: number;
  affected_items: number;
  orders: number[];
  first_at: string;
  last_at: string;
}

/**
 * The codes a plain "Retry failed" will NOT clear, mirrored from
 * BUILD_ERROR_CODES (src/lib/build/types.ts) for one purpose: labelling them in
 * the force-retry picker.
 *
 * DISPLAY HINT ONLY, and deliberately so. The picker offers every code currently
 * parked on the order, whatever this set says, so a permanent code added
 * server-side and not mirrored here loses its label and nothing else - it can
 * never lose the escape hatch itself. The server classifies; this only explains.
 */
/**
 * The three codes that never block delivery, mirrored from BUILD_ERROR_CODES.
 *
 * This one IS load-bearing, because `isParked` is defined against it: a code is
 * parked unless it is soft, and anything unregistered classifies transient (and
 * therefore parked) server-side. Mirroring the SOFT set rather than the parked
 * set reproduces that exactly, and drift can only ever paint a new soft code as
 * blocking - loud and visible, never a park quietly hidden from the operator.
 */
export const BUILD_SOFT_CODES: ReadonlySet<string> = new Set([
  'tracking_cert_pending',
  'forwarding_unproven',
  'dns_conflict',
]);

/** Mirrors isParked(): is this item's error a reason a station leaves it alone? */
export function buildIsParked(code: string | null): boolean {
  return code !== null && !BUILD_SOFT_CODES.has(code);
}

export const BUILD_PERMANENT_CODES: ReadonlySet<string> = new Set([
  'domain_already_in_use',
  'dns_required_conflict',
  'sequencer_google_app_blocked',
  'sequencer_google_wrong_password',
  'sequencer_account_deleted',
  'photo_source_purged',
  'web_login_required',
  'iap_challenge',
  'mi_wrong_workspace',
  'mi_workspace_undeclared',
  'mi_blocked_app',
  'sequencer_provider_unsupported',
  'sequencer_google_login_rejected',
  'sequencer_password_missing',
]);

/** What POST /orders answers with. The snapshot comes back REDACTED - the photo
 *  bytes the caller just sent are replaced by `images_count` and the address
 *  list, because echoing tens of megabytes of base64 proves nothing. */
export interface BuildOrderDraft {
  order: BuildOrderRow;
  snapshot: Record<string, unknown>;
  roster: { mailboxes: number; domains: number };
  notes?: string[];
}

export interface BuildLaunchResult {
  launched: boolean;
  allocated_panels: string[];
  shortfall: string[];
  allocation_error?: string;
}

/** From /api/settings/cloudflare-accounts. A Build Line profile must point at an
 *  `is_active: false` row: active rows ARE the order-purchase rotation pool, and
 *  a customer's account joining it would have us creating our zones inside it. */
export interface BuildCfAccount {
  id: number;
  name: string;
  account_id: string;
  is_active: boolean;
}

/* ─── Transport ─────────────────────────────────────────────────────────── */

/**
 * Its own fetch rather than the shared `api`, for one reason: the shared client
 * copies `body.data` onto the thrown ApiError, and every refusal on this surface
 * puts its `details[]` at `body.error.details`. Routed through `api`, every
 * "which field is wrong" line would be dropped and the wizard could only ever
 * say "order failed validation" - which is exactly the moment an operator needs
 * the list.
 *
 * NO TIMEOUT IS IMPOSED. The preflight runs inline for one to three minutes
 * (it reaches Cloudflare, the registrar, DWD, the sequencer and MasterInbox),
 * and an AbortSignal here would kill a report the server is still writing.
 */
async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init.headers },
  });
  let body: {
    success?: boolean;
    data?: unknown;
    error?: { code?: string; message?: string; details?: unknown };
  };
  try {
    body = await res.json();
  } catch {
    throw new ApiError('PARSE_ERROR', 'Failed to parse response', res.status);
  }
  if (!res.ok || body.success === false) {
    throw new ApiError(
      body.error?.code ?? 'UNKNOWN',
      body.error?.message ?? 'Request failed',
      res.status,
      body.error?.details,
    );
  }
  return body.data as T;
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return req<T>(path, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

/** Operator-facing text for any thrown value. Server messages here are built
 *  from row data and validation paths, never from a borrowed credential. */
export function buildErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Request failed';
}

/** The per-field refusals behind an `invalid_payload` / `invalid_enrollment`. */
export function buildIssues(err: unknown): BuildEnrollmentIssue[] {
  const data = err instanceof ApiError ? err.data : undefined;
  if (!Array.isArray(data)) return [];
  return data.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return [];
    const { path, message } = entry as { path?: unknown; message?: unknown };
    if (typeof path !== 'string' || typeof message !== 'string') return [];
    return [{ path, message }];
  });
}

/* ─── Calls ─────────────────────────────────────────────────────────────── */

export interface BuildOrderPayload {
  profile_id: number;
  label: string;
  roster_csv: string;
  forwarding_url?: string | null;
  overrides: {
    panel_bucket?: BuildPanelBucket;
    serken_percent?: number;
    registrar?: BuildRegistrar;
    sequencer?: {
      provider?: BuildSequencerProvider;
      settings?: Record<string, unknown>;
      optional_tags?: string[];
      group_tags?: boolean;
      tracking_attach?: boolean;
      sl_user_id?: string;
      base_url?: string;
      workspace_id?: string;
      pv_workspace_id?: string;
    };
    masterinbox?: { enabled?: boolean; app_id?: string; workspace_id?: number };
    photos?: { enabled?: boolean; images?: Record<string, BuildPhotoEntry> };
  };
  secrets: {
    registrar_creds?: Record<string, string>;
    sequencer_api_key?: string;
    masterinbox_api_key?: string;
  };
}

/** One row of GET /runs - a station invocation, coordinator-fired or standalone. */
export interface BuildStationRunListRow {
  id: number;
  station: string;
  order_id: number | null;
  order_label: string | null;
  initiated_by: string;
  scope: Record<string, unknown> | null;
  results: Record<string, unknown> | null;
  started_at: string;
  finished_at: string | null;
}

/** Mirrors BUILD_ORACLE_KINDS in src/processors/build/stations/oracles.ts -
 *  the server validates against its own list, this one just fills the picker. */
export const BUILD_ORACLE_KINDS = [
  'seats', 'domains', 'dns', 'logins', 'sequencer', 'masterinbox',
] as const;

export const buildApi = {
  workerWarnings: () => req<{ warnings: BuildWorkerWarning[] }>('/worker-warnings'),
  listProfiles: (): Promise<BuildProfile[]> =>
    req<{ profiles: BuildProfile[] }>('/profiles').then((d) => d.profiles),

  listRuns: (opts: { orderId?: number; station?: string; limit?: number } = {}): Promise<BuildStationRunListRow[]> => {
    const params = new URLSearchParams();
    if (opts.orderId) params.set('order_id', String(opts.orderId));
    if (opts.station) params.set('station', opts.station);
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return req<{ runs: BuildStationRunListRow[] }>(`/runs${qs ? `?${qs}` : ''}`).then((d) => d.runs);
  },

  /** Live governor utilization. `in_use: null` = the bucket was unreadable
   *  this instant, never a failed view. */
  listGovernors: (): Promise<{ key: string; in_use: number | null; limit: number }[]> =>
    req<{ governors: { key: string; in_use: number | null; limit: number }[] }>('/governors')
      .then((d) => d.governors),

  /** Oracles only in v1 - the server refuses every mutating station. */
  runOracle: (orderId: number, kind: string): Promise<{ pass: boolean; detail: unknown }> =>
    post<{ result: { pass: boolean; detail: unknown } }>('/runs', {
      station: 'oracle', kind, order_id: orderId,
    }).then((d) => d.result),

  createProfile: (input: BuildProfileInput): Promise<BuildProfile> =>
    post<{ profile: BuildProfile }>('/profiles', input).then((d) => d.profile),

  updateProfile: (id: number, input: BuildProfileInput): Promise<BuildProfile> =>
    req<{ profile: BuildProfile }>(`/profiles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }).then((d) => d.profile),

  /** Keys are optional and are never stored. Sent only when the operator has
   *  actually typed one; the Cloudflare leg always runs. */
  probeProfile: (
    id: number,
    keys: {
      sequencer_api_key?: string;
      masterinbox_api_key?: string;
      sequencer_base_url?: string;
      resolve_smartlead_user_id?: boolean;
    } = {},
  ): Promise<BuildProbeReport> =>
    post<{ probe: BuildProbeReport }>(`/profiles/${id}/probe`, keys).then((d) => d.probe),

  createOrder: (payload: BuildOrderPayload): Promise<BuildOrderDraft> =>
    post<BuildOrderDraft>('/orders', {
      ...payload,
      overrides: {
        ...payload.overrides,
        ...(payload.overrides.photos ? { photos: compactBuildPhotos(payload.overrides.photos) } : {}),
      },
    }),

  /** Runs inline for one to three minutes. See the transport note. */
  preflight: (orderId: number): Promise<BuildPreflightReport> =>
    post<{ report: BuildPreflightReport }>(`/orders/${orderId}/preflight`).then((d) => d.report),

  launch: (orderId: number): Promise<BuildLaunchResult> =>
    post<BuildLaunchResult>(`/orders/${orderId}/launch`),

  listOrders: (filters?: BuildOrderFilters): Promise<BuildOrderListRow[]> => {
    const query = filters ? `?${new URLSearchParams({
      search: filters.search.trim(), status: filters.status,
      exclude_aborted: String(filters.exclude_aborted),
    })}` : '';
    return req<{ orders: BuildOrderListRow[] }>(`/orders${query}`).then((d) => d.orders);
  },

  /** Every parked item across every LIVE order - the operator's inbox. Its own
   *  endpoint rather than a fold over the order list: the list carries counts
   *  only, and the strip needs the codes. */
  listAttention: (): Promise<BuildAttentionRow[]> =>
    req<{ attention: BuildAttentionRow[] }>('/attention').then((d) => d.attention),

  getOrder: (orderId: number): Promise<BuildOrderDetail> =>
    req<BuildOrderDetail>(`/orders/${orderId}`),

  /**
   * Put failed items back. The default clears TRANSIENT codes only; naming codes
   * is the R26 escape hatch, for when the operator has changed the world outside
   * the pipeline and a refusal that was settled this morning is not settled any
   * more. An empty list is sent as no list at all, so a cleared picker cannot be
   * read as "retry nothing".
   */
  retryFailed: (orderId: number, includeCodes: string[] = []): Promise<{ cleared: number }> =>
    post<{ cleared: number }>(
      `/orders/${orderId}/retry-failed`,
      includeCodes.length > 0 ? { include_codes: includeCodes } : {},
    ),

  continueAnyway: (orderId: number): Promise<{ abandoned: { domains: number; mailboxes: number } }> =>
    post<{ abandoned: { domains: number; mailboxes: number } }>(`/orders/${orderId}/continue`),

  /** Arm the hourly retry of the held domains for `hours` (1..72). The server
   *  refuses (409) when nothing on the order is held. */
  armReleaseRetry: (orderId: number, hours: number): Promise<{ release_retry_until: string; held: number }> =>
    post<{ release_retry_until: string; held: number }>(`/orders/${orderId}/release-retry`, { hours }),

  stopReleaseRetry: (orderId: number): Promise<{ armed: boolean }> =>
    req<{ armed: boolean }>(`/orders/${orderId}/release-retry`, { method: 'DELETE' }),

  /** The reason is optional but recorded on the order's history when given. */
  abortOrder: (orderId: number, reason?: string): Promise<{ aborted: boolean }> =>
    post<{ aborted: boolean }>(`/orders/${orderId}/abort`, reason?.trim() ? { reason: reason.trim() } : {}),

  /** "Re-run QA": the whole delivery report again, on a delivered or closed
   *  order. Queued, not inline - the replacement report shows up on the next
   *  detail read once the run lands. */
  rerunDeliveryReport: (orderId: number): Promise<{ enqueued: boolean }> =>
    post<{ enqueued: boolean }>(`/orders/${orderId}/delivery-report`),

  /** The existing settings surface, not a Build Line endpoint. */
  listCloudflareAccounts: (): Promise<BuildCfAccount[]> =>
    api<BuildCfAccount[]>('/api/settings/cloudflare-accounts'),
};

/* ─── Snapshot helpers (shared by the wizard and the profile form) ──────── */

/**
 * `2026_08_30` - the label shape the live Instantly profiles carry
 * (`PI_2026_08_13`), UTC so two operators in two timezones tag one batch the
 * same way. Mirrors dateToken() server-side.
 */
export function dateToken(at: Date = new Date()): string {
  return at.toISOString().slice(0, 10).replace(/-/g, '_');
}

/** Applied per mailbox by the server to subdomain mailboxes only, so it is
 *  never offered as a run-wide tag (src/processors/sequencer-connect-shared.ts). */
export const SUBDOMAIN_TAG = 'Sub-Domain';

/**
 * The tag list an order would ship with, resolved.
 *
 * The sequencer station writes what the snapshot gives it verbatim, so
 * `PI_{DATE}` has to become a real label at enrollment or the customer's
 * workspace grows a tag with a literal brace in it. Standard tags come first
 * and always; the operator's picks are appended in the order chosen, deduped
 * case-sensitively (Instantly matches labels exactly). Mirrors resolveTags();
 * the copy here exists so the review step can SHOW what the server will write.
 */
export function resolveTags(standard: string[], selected: string[], at: Date = new Date()): string[] {
  const token = dateToken(at);
  const out: string[] = [];
  for (const tag of [...standard, ...selected]) {
    const resolved = tag.replace(/\{DATE\}/g, token).trim();
    if (resolved && !out.includes(resolved)) out.push(resolved);
  }
  return out;
}

/** Records are written at the registrar (`registrar`, legacy `spaceship`).
 *  Mirrors hostsAtRegistrar() in src/lib/build/dns-settings.ts. */
export function hostsAtRegistrar(dnsMode: BuildDnsMode): boolean {
  return dnsMode === 'spaceship' || dnsMode === 'registrar';
}

/** The registrar a registrar-DNS profile writes at, or null when the profile
 *  is Cloudflare-hosted. A legacy `spaceship` row is always Spaceship. */
export function registrarDnsOf(profile: Pick<BuildProfile, 'dns_mode' | 'registrar_default'>): BuildRegistrar | null {
  if (profile.dns_mode === 'spaceship') return 'spaceship';
  return profile.dns_mode === 'registrar' ? profile.registrar_default : null;
}

/**
 * Will this order's build ever authenticate at a registrar?
 *
 * Always under registrar DNS: the credential writes the records. Otherwise
 * only when a zone has to be DELEGATED. `cf_held` means the zones are already
 * ours, and `none` means the customer routed the domains themselves - either
 * way a credential nothing will use is one we should not borrow, and the
 * enrollment refuses to store it. Mirrors registrarIsUsed().
 */
export function registrarIsUsed(dnsMode: BuildDnsMode, registrar: BuildRegistrar): boolean {
  if (hostsAtRegistrar(dnsMode)) return true;
  return dnsMode !== 'cf_held' && registrar !== 'none';
}

/** The Cold Labs Instantly baseline, verbatim from the profile proven 360/360 on
 *  2026-08-13 (automation_development/instantly/profiles/cold-labs.json, and the
 *  fixture in src/lib/px3-sequencer-params.test.ts). Offered as the starting
 *  point for a new `instantly` profile so nobody retypes it from memory - the
 *  ftag in particular is a workspace-specific value that must be checked. */
export const COLD_LABS_INSTANTLY_SETTINGS: Record<string, unknown> = {
  daily_limit: 15,
  sending_gap: 5,
  enable_slow_ramp: true,
  warmup: {
    limit: 12,
    increment: 3,
    reply_rate: 42,
    warmup_custom_ftag: 'qjv9ybew',
    advanced: {
      open_rate: 70,
      spam_save_rate: 70,
      important_rate: 15,
      warm_ctd: false,
      read_emulation: true,
      weekday_only: false,
    },
  },
};

/** A blank profile, with the same defaults the migration's column defaults and
 *  the router's zod schema carry, so "create" and "the server's idea of a new
 *  profile" never disagree. */
export function emptyProfileInput(): BuildProfileInput {
  return {
    name: '',
    customer: '',
    dns_mode: 'cf_held',
    cf_account_id: null,
    registrar_default: 'none',
    records: {
      write_mx: true,
      dmarc_policy: 'v=DMARC1; p=none;',
      spf: PREVIEW_SENDING_SPF,
      tracking_prefix: 'inst',
      tracking_target: '',
      // Mirrors the server schema's create defaults so a fresh profile behaves
      // exactly as the shipped one did, AND so the operator sees the two tracking
      // CNAMEs in the form before saving.
      extra_records: [
        { type: 'CNAME', host: 'inst', content: 'prox.itrackly.com', proxied: false, enabled: true },
        { type: 'CNAME', host: 'trk', content: 'open.sleadtrack.com', proxied: false, enabled: true },
      ],
    },
    panel_bucket: 'serken',
    serken_percent: 50,
    allocation_policy: 'leave_0_or_2',
    sequencer_provider: 'instantly',
    sequencer_settings: {},
    tag_scheme: { standard_tags: [], optional_tags: [], group_tags_enabled: false },
    masterinbox: { enabled_default: false, app_id: MI_ALLOWED_APP_ID },
    forwarding: { require_target_url: false, enabled: true },
    photos: { enabled_default: true, visibility_pass: false },
    ctd_default: true,
    notes: '',
  };
}

/** The editable half of a saved profile, for the edit form. Legacy rows written
 *  before `spf` / `extra_records` / `forwarding.enabled` / `ctd_default` existed
 *  are normalised so the form never renders against an undefined field. */
export function profileToInput(profile: BuildProfile): BuildProfileInput {
  const { id: _id, created_at: _created, updated_at: _updated, ...input } = profile;
  return {
    ...input,
    records: {
      write_mx: input.records.write_mx,
      dmarc_policy: input.records.dmarc_policy,
      spf: input.records.spf ?? PREVIEW_SENDING_SPF,
      tracking_prefix: input.records.tracking_prefix,
      tracking_target: input.records.tracking_target,
      extra_records: input.records.extra_records ?? [],
    },
    forwarding: {
      require_target_url: input.forwarding.require_target_url,
      enabled: input.forwarding.enabled ?? true,
    },
    ctd_default: input.ctd_default ?? true,
    serken_percent: input.serken_percent ?? 50,
  };
}

/** An independent create draft, named to avoid the profiles already loaded.
 *  Reserve room for the suffix within the API's 100-character name limit. */
export function duplicateProfileInput(
  profile: BuildProfile,
  profiles: readonly Pick<BuildProfile, 'name'>[],
): BuildProfileInput {
  const input = structuredClone(profileToInput(profile));
  const names = new Set(profiles.map((row) => row.name));
  names.add(profile.name);
  for (let copy = 1; ; copy += 1) {
    const suffix = copy === 1 ? '-copy' : `-copy-${copy}`;
    const name = `${profile.name.slice(0, 100 - suffix.length)}${suffix}`;
    if (!names.has(name)) {
      input.name = name;
      return input;
    }
  }
}

/* ─── DNS preview (mirror of src/lib/build/dns-plan.ts + foreign-cf-normalize.ts) ─
 *
 * Replicated here rather than imported: dns-plan.ts is server code (ESM `.js`
 * import specifiers, a type edge to the repo module) and the frontend keeps its
 * own faithful copy the way it mirrors dateToken / resolveTags above. The LIVE
 * SUMMARY has to match planSendingRecordsFor exactly or it lies about what lands
 * on Cloudflare, so any change to the planner's naming or ordering must be echoed
 * here.
 */

/** The default SPF, and the fallback the planner uses when a profile leaves
 *  `records.spf` blank. Mirrors SENDING_SPF. */
export const PREVIEW_SENDING_SPF = 'v=spf1 include:_spf.google.com ~all';
const PREVIEW_GOOGLE_MX_HOST = 'smtp.google.com';
const PREVIEW_FORWARDING_DUMMY_IP = '192.0.2.1';

/** Mirrors KNOWN_DOUBLE_SUFFIXES in foreign-cf-normalize.ts. */
const PREVIEW_DOUBLE_SUFFIXES = ['co.uk', 'co.nz', 'co.za', 'com.au', 'com.br', 'org.uk'];

function previewSplitRoot(domain: string): { zoneRoot: string; recordPrefix: string | null } {
  const parts = domain.split('.');
  const lastTwo = parts.slice(-2).join('.');
  const rootLabels = PREVIEW_DOUBLE_SUFFIXES.includes(lastTwo) ? 3 : 2;
  if (parts.length <= rootLabels) return { zoneRoot: domain, recordPrefix: null };
  return { zoneRoot: parts.slice(-rootLabels).join('.'), recordPrefix: parts.slice(0, -rootLabels).join('.') };
}

/** Mirrors recordName(). */
function previewRecordName(shortName: string, recordPrefix: string | null): string {
  if (!recordPrefix) return shortName;
  return shortName === '@' ? recordPrefix : `${shortName}.${recordPrefix}`;
}

/** Mirrors isServableSub(): Universal SSL covers the apex and ONE label below it. */
function previewIsServableSub(prefix: string | null): boolean {
  return prefix !== null && !prefix.includes('.');
}

function previewQuote(value: string): string {
  return `"${value.trim()}"`;
}

/** One record row in the summary. TXT `content` arrives ALREADY QUOTED, exactly
 *  as planSendingRecordsFor emits it. */
export interface PreviewRecord {
  type: BuildExtraRecord['type'];
  /** Zone-relative name (`@`, `www`, `_dmarc`, `inst.go`), the recordName shape. */
  name: string;
  content: string;
  proxied?: boolean;
  priority?: number;
}

export interface PreviewGroup {
  key: 'baseline' | 'extras' | 'forwarding' | 'dynamic';
  label: string;
  note?: string;
  records: PreviewRecord[];
}

/**
 * The exact CF record set this profile produces for one sample domain, grouped
 * for the summary. Baseline (MX/SPF/DMARC) + enabled extras + forwarding (only
 * when enabled) come straight from planSendingRecordsFor; the Dynamic group is
 * the google-site-verification TXT and the google._domainkey DKIM TXT that the
 * domain-add and DKIM stations write PER DOMAIN, shown at the same recordName so
 * the operator sees the whole zone, not just the profile-configured half.
 */
export function previewRecordsFor(
  records: BuildProfile['records'],
  forwardingEnabled: boolean,
  domain: string,
): PreviewGroup[] {
  const host = domain.trim().toLowerCase().replace(/\.$/, '');
  const { recordPrefix: prefix } = previewSplitRoot(host);
  const isRoot = prefix === null;

  const baseline: PreviewRecord[] = [];
  if (records.write_mx) {
    baseline.push({ type: 'MX', name: previewRecordName('@', prefix), content: PREVIEW_GOOGLE_MX_HOST, priority: 1 });
  }
  baseline.push({ type: 'TXT', name: previewRecordName('@', prefix), content: previewQuote(records.spf || PREVIEW_SENDING_SPF) });
  baseline.push({ type: 'TXT', name: previewRecordName('_dmarc', prefix), content: previewQuote(records.dmarc_policy) });

  const extras: PreviewRecord[] = (records.extra_records ?? [])
    .filter((r) => r.enabled)
    .map((extra) => ({
      type: extra.type,
      name: previewRecordName(extra.host, prefix),
      content: extra.type === 'TXT' ? previewQuote(extra.content) : extra.content,
      proxied: extra.proxied,
      priority: extra.priority,
    }));

  const forwarding: PreviewRecord[] = [];
  if (forwardingEnabled) {
    if (isRoot) {
      forwarding.push({ type: 'A', name: '@', content: PREVIEW_FORWARDING_DUMMY_IP, proxied: true });
      forwarding.push({ type: 'A', name: 'www', content: PREVIEW_FORWARDING_DUMMY_IP, proxied: true });
    } else if (previewIsServableSub(prefix)) {
      forwarding.push({ type: 'A', name: previewRecordName('@', prefix), content: PREVIEW_FORWARDING_DUMMY_IP, proxied: true });
    }
  }

  const dynamic: PreviewRecord[] = [
    { type: 'TXT', name: previewRecordName('@', prefix), content: previewQuote('google-site-verification=<token>') },
    { type: 'TXT', name: previewRecordName('google._domainkey', prefix), content: previewQuote('v=DKIM1; k=rsa; p=<published per domain>') },
  ];

  const groups: PreviewGroup[] = [
    { key: 'baseline', label: 'Baseline', records: baseline },
    { key: 'extras', label: 'Extras', records: extras },
  ];
  if (forwardingEnabled) {
    groups.push({
      key: 'forwarding',
      label: 'Forwarding',
      note: isRoot
        ? 'plus a 301 redirect rule covering @ and www'
        : previewIsServableSub(prefix)
          ? 'plus a 301 redirect rule covering this host'
          : 'this subdomain is too deep for Universal SSL - no proxied A record and no redirect clause',
      records: forwarding,
    });
  }
  groups.push({ key: 'dynamic', label: 'Added per domain automatically', records: dynamic });
  return groups;
}
